import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";

import { selectUser } from "@/store/slices/authSlice";
import { addAlert, updateAlert } from "@/store/slices/alertSlice";
import { addWorkOrder, updateWorkOrder } from "@/store/slices/workOrderSlice";
import { updateEquipment } from "@/store/slices/equipmentSlice";
import { addNotification } from "@/store/slices/uiSlice";
import { WebSocketMessage } from "@/types";

export default function useWebSocket() {
  // Check if WebSocket is enabled
  const isWebSocketEnabled = import.meta.env.VITE_ENABLE_WEBSOCKETS === "true";

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { getToken, isSignedIn } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connectWebSocket = async () => {
    if (!isSignedIn || !user) return;

    const wsUrl = import.meta.env.VITE_WS_URL || "ws://localhost:3001";

    try {
      // Get Clerk session token
      const token = await getToken();
      if (!token) return;

      wsRef.current = new WebSocket(`${wsUrl}?token=${token}`);

      wsRef.current.onopen = () => {
        if (import.meta.env.DEV) {
          console.debug("WebSocket connected");
        }
        reconnectAttempts.current = 0;

        // Send user info to establish connection
        wsRef.current?.send(
          JSON.stringify({
            type: "auth",
            payload: { userId: user.id },
          })
        );
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (error) {
          if (import.meta.env.DEV) {
            console.debug("Error parsing WebSocket message:", error);
          }
        }
      };

      wsRef.current.onerror = (error) => {
        if (import.meta.env.DEV) {
          console.debug("WebSocket error:", error);
        }
        toast.error("WebSocket connection error");
      };

      wsRef.current.onclose = (event) => {
        if (import.meta.env.DEV) {
          console.debug("WebSocket disconnected", event.code, event.reason);
        }

        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (import.meta.env.DEV) {
              console.debug(`Reconnecting WebSocket (attempt ${reconnectAttempts.current})...`);
            }
            connectWebSocket();
          }, delay);
        }
      };
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug("Failed to connect WebSocket:", error);
      }
    }
  };

  const handleWebSocketMessage = (message: WebSocketMessage) => {
    switch (message.type) {
      case "alert":
        if (message.payload.action === "created") {
          dispatch(addAlert(message.payload.alert));
          toast.info(`New alert: ${message.payload.alert.title}`);
        } else if (message.payload.action === "updated") {
          dispatch(updateAlert(message.payload.alert));
        }
        break;

      case "work_order_update":
        if (message.payload.action === "created") {
          dispatch(addWorkOrder(message.payload.workOrder));
          toast.info(`New work order: ${message.payload.workOrder.title}`);
        } else if (message.payload.action === "updated") {
          dispatch(updateWorkOrder(message.payload.workOrder));
        }
        break;

      case "equipment_update":
        dispatch(updateEquipment(message.payload.equipment));
        break;

      case "notification":
        dispatch(addNotification(message.payload.notification));
        toast.info(message.payload.notification.message);
        break;

      default:
        if (import.meta.env.DEV) {
          console.debug("Unknown WebSocket message type:", message.type);
        }
    }
  };

  const disconnectWebSocket = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, "Component unmounting");
      wsRef.current = null;
    }
  };

  useEffect(() => {
    if (!isWebSocketEnabled) {
      if (import.meta.env.DEV) {
        console.debug("WebSocket is disabled");
      }
      return;
    }

    if (isSignedIn && user) {
      connectWebSocket();
    }

    return () => {
      disconnectWebSocket();
    };
  }, [isSignedIn, user, isWebSocketEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectWebSocket();
    };
  }, []);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
    sendMessage: (message: any) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(message));
      } else {
        if (import.meta.env.DEV) {
          console.debug("WebSocket is not connected");
        }
      }
    },
  };
}
